import { useState } from 'react';
import KaliTaskOutputViewer from '../../../common/KaliTaskOutputViewer';
import { ChevronDown, ChevronRight } from 'lucide-react';

/**
 * The stream: every turn the agent took, in order.
 *
 * This is the audit trail rendered honestly rather than a highlight reel.
 * Refusals are shown as prominently as successes, because a refusal is the gate
 * working and an operator reviewing an engagement needs to see where the agent
 * pushed and was stopped.
 */

const KIND_TONE = {
  probe: 'bg-sky-50 text-sky-800 border-sky-200',
  run_tool: 'bg-violet-50 text-violet-800 border-violet-200',
  record_evidence: 'bg-slate-50 text-slate-700 border-slate-200',
  update_hypothesis: 'bg-wasabi-50 text-wasabi-700 border-wasabi-200',
  emit_hypothesis: 'bg-cyan-50 text-cyan-800 border-cyan-200',
  raise_authority: 'bg-amber-50 text-amber-800 border-amber-200',
  conclude: 'bg-indigo-50 text-indigo-800 border-indigo-200',
};

function Turn({ turn }) {
  const [open, setOpen] = useState(false);
  const inner = (turn.action || {}).action || {};
  const thinking = (turn.action || {}).thinking || '';
  const denied = turn.outcome === 'denied';
  const errored = turn.outcome === 'error';

  return (
    <div
      className={`rounded-lg border p-2.5 ${
        denied ? 'border-amber-300 bg-amber-50/40'
          : errored ? 'border-red-200 bg-red-50/40'
          : 'border-slate-200 bg-white'
      }`}
    >
      <div className="flex items-start gap-2">
        <span className="mt-0.5 w-6 shrink-0 text-right font-mono text-[11px] text-slate-400">
          {turn.seq}
        </span>
        <span
          className={`shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-semibold ${
            KIND_TONE[turn.action_kind] || 'bg-slate-50 text-slate-700 border-slate-200'
          }`}
        >
          {turn.action_kind}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs leading-snug text-slate-800">{inner.rationale}</p>

          {turn.result_summary && !denied && (
            <p className="mt-1 font-mono text-[11px] leading-snug text-slate-500">
              {turn.result_summary}
            </p>
          )}

          {denied && (
            <p className="mt-1 text-[11px] leading-snug text-amber-900">
              <span className="font-semibold">Refused: </span>
              {turn.denial_reason}
            </p>
          )}
          {errored && (
            <p className="mt-1 text-[11px] leading-snug text-red-800">
              <span className="font-semibold">Error: </span>
              {turn.result_summary}
            </p>
          )}

          {thinking && (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-slate-400 hover:text-slate-700"
            >
              {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              reasoning
            </button>
          )}
          {open && (
            <p className="mt-1 whitespace-pre-wrap rounded bg-slate-50 p-2 text-[11px] leading-relaxed text-slate-600">
              {thinking}
            </p>
          )}
        </div>
        <span className="shrink-0 font-mono text-[10px] text-slate-400">
          {(turn.input_tokens || 0) + (turn.output_tokens || 0)}t
        </span>
      </div>
    </div>
  );
}

//: The beacon task behind a turn, when there is one.
//
//: A `run_tool` turn records its evidence row, and the evidence row carries
//: the beacon task id. That is the only link between "the agent ran ffuf" and
//: the stdout the beacon streamed back line by line while it ran.
function taskIdFor(turn, evidence) {
  if (!turn?.evidence_ids?.length || !evidence?.length) return null;
  const ids = new Set(turn.evidence_ids);
  const row = evidence.find((e) => ids.has(e.id) && e.beacon_task_id);
  return row?.beacon_task_id || null;
}

export default function StreamView({ turns, evidence }) {
  if (!turns?.length) {
    // NAMED DIFFERENTLY FROM THE EVIDENCE PANE ON PURPOSE. Angles and Stream
    // share the rail and the angle header, so when both bodies said a bland
    // one-liner the two tabs were indistinguishable and the operator asked
    // why there were two of them. Each empty state now says what THIS tab is
    // for, which is the only thing on screen that separates them.
    return (
      <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center">
        <p className="text-sm font-medium text-slate-600">
          No turns yet
        </p>
        <p className="mx-auto mt-1 max-w-md text-xs text-slate-500">
          This is the agent&rsquo;s reasoning, turn by turn: what it chose to do,
          why, and what the gate said. The first line appears as soon as it
          takes its opening turn.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      {turns.map((t) => (
        <div key={t.id} className="motion-safe:animate-row-arrive">
          <Turn turn={t} />
          {/* LIVE STDOUT, BEHIND A TOGGLE. A wordlist scan is minutes of
              silence otherwise, and on pentest 44 the operator watched a
              15-minute ffuf with nothing on screen to show it was alive or
              worth waiting for. Collapsed by default: this is for the one
              task you are waiting on, not a firehose on every turn. */}
          {t.action_kind === 'run_tool' && taskIdFor(t, evidence) && (
            <div className="ml-6 mt-1">
              <KaliTaskOutputViewer taskId={taskIdFor(t, evidence)} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
